<template>
  <div class="card">
    <div class="applicant-advance-filter" layout="column">
      <div class="mb-x3" layout="row" layout-align="start center">
        <div class="search-form mr-x1" layout="column">
          <div class="label mb-x1">{{ accountName.label }}</div>
          <el-input
            type="text"
            v-model="accountName.value"
            @change="onChangeAccountName"
            :placeholder="accountName.placeholder"
          >
            <template slot="prepend"><i class="mdi mdi-magnify"></i></template>
          </el-input>
        </div>
        <select-form
          :label="winnerContact.label"
          :icon="winnerContact.icon"
          :value="winnerContact.value"
          :options="winnerContact.options"
          :onChange="onChangeWinnerContact"
          clearable
        ></select-form>
      </div>
      <div class="mb-x3" layout>
        <div class="date-range-form" layout="column">
          <div class="label mb-x1">応募期間</div>
          <AppDateRangePicker
            :value="entryDateRange.value"
            :campaign="campaign"
            :campaign-prize="campaignPrize"
            @change="onDateRangeChange"
            :placeholder="['開始日時', '終了日時']"
          />
        </div>
      </div>
      <div class="mb-x3" layout>
        <input-range-form label="応募数" :value="postsCount.value" @input="changePostsCount"></input-range-form>
        <input-range-form
          v-if="campaignAccountId"
          label="累計参加数"
          :value="totalCampaignEntryCount.value"
          @input="changeTotalCampaignEntryCount"
        ></input-range-form>
        <input-range-form
          v-if="campaignAccountId"
          label="累計当選数"
          :value="totalCampaignWinningCount.value"
          @input="changeTotalCampaignWinningCount"
        ></input-range-form>
      </div>
      <div layout layout-align="start-center">
        <el-button type="primary" @click="changeAdvanceFilter" :disabled="isLoading"
          ><i class="mdi mdi-plus"></i>詳細条件を追加して検索</el-button
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.applicant-advance-filter {
  .search-form {
    .el-input {
      width: $spacing-unit * 37;
    }
  }

  .select-form {
    margin-right: $spacing-unit;
  }

  :deep(.el-input__inner) {
    height: $spacing-unit * 4;
  }

  :deep(.el-input) {
    width: $spacing-unit * 18;
  }
}
</style>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import { AppDateRangePicker } from '@/client/components/_atoms'
import InputRangeForm from '@/client/components/_atoms/input_range_form'
import SelectForm from '@/client/components/_atoms/select_form'
import { TrackingService } from '@/client/services'
import { useInstagramApplicantStore } from '@/client/store/useInstagramApplicantStore'

export default defineComponent({
  components: {
    SelectForm,
    InputRangeForm,
    AppDateRangePicker
  },
  created() {
    this.accountName.value = this.params.accountName
    this.winnerContact.value = this.params.isWinnerContact
    this.postsCount.value = [...this.params.postsCount]
    this.totalCampaignEntryCount.value = [...this.params.totalCampaignEntryCount]
    this.totalCampaignWinningCount.value = [...this.params.totalCampaignWinningCount]
    // 既存のパラメータがある場合はそれを使用
    if (this.params.entryDateFrom || this.params.entryDateTo) {
      this.entryDateRange.value = [this.params.entryDateFrom || '', this.params.entryDateTo || '']
    } else {
      // デフォルト値は空にしておく（AppDateRangePickerで設定）
      this.entryDateRange.value = ['', '']
    }
  },
  data() {
    return {
      // アカウント名検索
      accountName: {
        value: '',
        label: 'アカウント名検索',
        placeholder: '検索(完全一致)'
      },
      // 当選連絡
      winnerContact: {
        value: '' as number | '',
        label: '当選連絡',
        icon: '',
        options: [
          {
            label: '指定なし',
            value: ''
          },
          {
            label: '当選連絡フラグあり',
            value: 1
          },
          {
            label: '当選連絡フラグなし',
            value: 0
          }
        ]
      },
      // 応募数
      postsCount: {
        value: ['', ''] as [number | '', number | '']
      },
      // 累計参加数
      totalCampaignEntryCount: {
        value: ['', ''] as [number | '', number | '']
      },
      // 累計当選数
      totalCampaignWinningCount: {
        value: ['', ''] as [number | '', number | '']
      },
      // 応募期間
      entryDateRange: {
        value: ['', '']
      },
      tempStartDate: null
    }
  },
  computed: {
    ...mapState(useInstagramApplicantStore, {
      params: state => state.params,
      campaign: state => state.campaign,
      campaignPrize: state => state.campaignPrize,
      campaignAccountId: state => (state.campaign && state.campaign.account ? state.campaign.account.id : ''),
      isLoading: state => state.isLoading
    })
  },
  methods: {
    ...mapActions(useInstagramApplicantStore, ['setParams', 'fetchApplicants']),
    onChangeAccountName(value: string) {
      TrackingService.sendEvent('input:応募一覧（IG）|アカウント名検索')

      this.accountName.value = value
    },
    onChangeWinnerContact(value: number | '') {
      TrackingService.sendEvent('select:応募一覧（IG）|当選連絡')

      this.winnerContact.value = value
    },
    changePostsCount(val: [number | '', number | '']) {
      if (this.postsCount.value[0] !== val[0]) {
        TrackingService.sendEvent('input:応募一覧（IG）|応募数|以上')
      } else if (this.postsCount.value[1] !== val[1]) TrackingService.sendEvent('input:応募一覧（IG）|応募数|未満')

      this.postsCount.value = val
    },
    changeTotalCampaignEntryCount(val: [number | '', number | '']) {
      if (this.totalCampaignEntryCount.value[0] !== val[0]) {
        TrackingService.sendEvent('input:応募一覧（IG）|累計参加数|以上')
      } else if (this.totalCampaignEntryCount.value[1] !== val[1])
        TrackingService.sendEvent('input:応募一覧（IG）|累計参加数|未満')

      this.totalCampaignEntryCount.value = val
    },
    changeTotalCampaignWinningCount(val: [number | '', number | '']) {
      if (this.totalCampaignWinningCount.value[0] !== val[0]) {
        TrackingService.sendEvent('input:応募一覧（IG）|累計当選数|以上')
      } else if (this.totalCampaignWinningCount.value[1] !== val[1])
        TrackingService.sendEvent('input:応募一覧（IG）|累計当選数|未満')

      this.totalCampaignWinningCount.value = val
    },
    onDateRangeChange(value: string[] | null) {
      TrackingService.sendEvent('select:応募一覧（IG）|応募期間')
      this.entryDateRange.value = value || ['', '']
    },
    async changeAdvanceFilter() {
      TrackingService.sendEvent('click:応募一覧（IG）|詳細条件を追加して検索')

      await this.setParams({
        accountName: this.accountName.value,
        isWinnerContact: this.winnerContact.value,
        postsCount: this.postsCount.value,
        totalCampaignEntryCount: this.totalCampaignEntryCount.value,
        totalCampaignWinningCount: this.totalCampaignWinningCount.value,
        entryDateFrom: this.entryDateRange.value[0] || '',
        entryDateTo: this.entryDateRange.value[1] || ''
      })

      await this.fetchApplicants()
    }
  }
})
</script>
